/**
 * Le dessin d'un plan de stand, en SVG : le même pour la régie (qui le
 * dessine) et pour le visiteur (qui y cherche sa table). Unités : mètres.
 */

export type TypeElement = "table" | "comptoir" | "ecran" | "mur" | "entree" | "salon" | "plante";

export interface ElementDessin {
  id?: string;
  type: TypeElement;
  x: number;
  y: number;
  w: number;
  h: number;
  rot: number;
  label: string;
  couleur?: string | null;
}

export interface PlanDessin {
  largeurM: number;
  profondeurM: number;
  elements: ElementDessin[];
}

/** Ce que chaque élément propose en le posant : nom, taille par défaut. */
export const MODELES: Record<TypeElement, { nom: string; w: number; h: number }> = {
  table: { nom: "Table", w: 1.2, h: 1.2 },
  comptoir: { nom: "Comptoir", w: 2, h: 0.7 },
  ecran: { nom: "Écran", w: 1.6, h: 0.2 },
  mur: { nom: "Cloison", w: 2, h: 0.12 },
  entree: { nom: "Entrée", w: 1.4, h: 0.3 },
  salon: { nom: "Salon", w: 2, h: 1.2 },
  plante: { nom: "Plante", w: 0.6, h: 0.6 },
};

export interface Theme {
  sol: string;
  trait: string;
  texte: string;
  meuble: string;
  texteMeuble: string;
  doux: string;
}

export const THEME_CLAIR: Theme = { sol: "#ffffff", trait: "#111111", texte: "#111111", meuble: "#1d1d1d", texteMeuble: "#ffffff", doux: "#ececec" };
export const THEME_SOMBRE: Theme = { sol: "#141414", trait: "#d9d9d9", texte: "#ffffff", meuble: "#e8e8e8", texteMeuble: "#111111", doux: "#2a2a2a" };

const rond = (e: ElementDessin): boolean => e.type === "plante" || (e.type === "table" && Math.abs(e.w - e.h) < 0.05);

/**
 * Le texte d'un élément : horizontal quand il tient (même si l'élément est
 * tourné), sinon couché le long de l'élément. Taille lisible, sans déborder.
 */
function texteDe(e: ElementDessin, texte: string): { fs: number; droit: boolean } {
  const n = Math.max(4, texte.length * 0.58);
  const etroit = Math.min(e.w, e.h);
  const haut = etroit < 0.3 ? 0.24 : etroit * 0.42;
  const quart = e.rot % 180 > 45 && e.rot % 180 < 135;
  // Horizontal à l'écran : la place visible est la largeur, ou la profondeur si l'élément est couché.
  const ecran = Math.min(0.32, ((quart ? e.h : e.w) * 0.9) / n, quart ? e.w * 0.42 : haut);
  if (ecran >= 0.16 || !quart) return { fs: Math.max(0.14, ecran), droit: true };
  // Sinon, le long de l'élément.
  return { fs: Math.max(0.14, Math.min(0.32, (e.w * 0.9) / n, haut)), droit: false };
}

export function Element(props: {
  e: ElementDessin;
  theme: Theme;
  /** La table du visiteur : mise en avant. */
  vive?: boolean;
  /** Les autres, quand une table est mise en avant. */
  terne?: boolean;
}): JSX.Element {
  const { e, theme } = props;
  const cx = e.x + e.w / 2;
  const cy = e.y + e.h / 2;
  const t = e.label || MODELES[e.type].nom;
  const { fs, droit } = texteDe(e, t);
  let forme: JSX.Element;
  let couleurTexte = theme.texte;
  switch (e.type) {
    case "table": {
      const c = e.couleur ?? "#8c8c8c";
      forme = rond(e) ? (
        <ellipse cx={cx} cy={cy} rx={e.w / 2} ry={e.h / 2} fill={c} />
      ) : (
        <rect x={e.x} y={e.y} width={e.w} height={e.h} rx={0.12} fill={c} />
      );
      couleurTexte = "#111111";
      break;
    }
    case "comptoir":
      forme = <rect x={e.x} y={e.y} width={e.w} height={e.h} rx={0.05} fill={theme.meuble} />;
      couleurTexte = theme.texteMeuble;
      break;
    case "ecran":
      forme = <rect x={e.x} y={e.y} width={e.w} height={e.h} rx={0.03} fill={theme.trait} />;
      break;
    case "mur":
      forme = <rect x={e.x} y={e.y} width={e.w} height={e.h} fill={theme.trait} />;
      break;
    case "entree":
      forme = (
        <g>
          <rect x={e.x} y={e.y} width={e.w} height={e.h} fill="none" stroke={theme.trait} strokeWidth={0.04} strokeDasharray="0.12 0.1" />
        </g>
      );
      break;
    case "salon":
      forme = <rect x={e.x} y={e.y} width={e.w} height={e.h} rx={0.25} fill={theme.doux} stroke={theme.trait} strokeWidth={0.02} />;
      break;
    case "plante":
      forme = <ellipse cx={cx} cy={cy} rx={e.w / 2} ry={e.h / 2} fill="#3f8f5a" />;
      break;
  }
  // Le nom des éléments fins (écran, cloison, entrée) se lit à côté, pas dessus.
  const fin = e.type === "ecran" || e.type === "mur" || e.type === "entree";
  const montrerNom = e.type !== "plante" && !(e.type === "mur" && !e.label);
  return (
    <g transform={`rotate(${e.rot} ${cx} ${cy})`} opacity={props.terne ? 0.45 : 1}>
      {props.vive ? (
        rond(e) ? (
          <ellipse cx={cx} cy={cy} rx={e.w / 2 + 0.22} ry={e.h / 2 + 0.22} fill="none" stroke={e.couleur ?? theme.trait} strokeWidth={0.08} className="plan-vive" />
        ) : (
          <rect x={e.x - 0.22} y={e.y - 0.22} width={e.w + 0.44} height={e.h + 0.44} rx={0.3} fill="none" stroke={e.couleur ?? theme.trait} strokeWidth={0.08} className="plan-vive" />
        )
      ) : null}
      {forme}
      {montrerNom ? (
        <text
          x={cx}
          y={fin ? e.y + e.h + fs * 1.1 : cy}
          transform={e.rot && droit ? `rotate(${-e.rot} ${cx} ${fin ? e.y + e.h + fs * 1.1 : cy})` : undefined}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fs}
          fontWeight={700}
          fill={fin ? theme.texte : couleurTexte}
          style={{ fontFamily: "'Host Grotesk', 'Atkinson Hyperlegible', Arial, sans-serif", pointerEvents: "none" }}
        >
          {t}
        </text>
      ) : null}
    </g>
  );
}

/** Le plan en lecture : pour le visiteur, sa table en avant. */
export function Plan(props: { plan: PlanDessin; vive?: (e: ElementDessin) => boolean; theme?: Theme; titre?: string }): JSX.Element {
  const theme = props.theme ?? THEME_CLAIR;
  const { largeurM: W, profondeurM: H } = props.plan;
  const m = 0.4;
  const uneVive = props.vive ? props.plan.elements.some(props.vive) : false;
  return (
    <svg viewBox={`${-m} ${-m} ${W + 2 * m} ${H + 2 * m}`} role="img" aria-label={props.titre ?? "Plan du stand"} style={{ display: "block", width: "100%", height: "auto" }}>
      <rect x={0} y={0} width={W} height={H} fill={theme.sol} stroke={theme.trait} strokeWidth={0.03} rx={0.06} />
      {props.plan.elements.map((e, i) => {
        const vive = props.vive?.(e) ?? false;
        return <Element key={e.id ?? i} e={e} theme={theme} vive={vive} terne={uneVive && !vive && e.type === "table"} />;
      })}
    </svg>
  );
}
